import {
  FlowController,
  getPageConfig,
  guardPageAccess,
  isZoneCompleted,
  markZoneAsCompleted,
  handleFinalCodeGeneration,
  handleManualRetry,
} from './flow';

import {
  validateIntroForm,
  validateZoneCode,
} from './validators';

import {
  setupBranchQuestion,
  setupInputMasks,
} from './ui';

import {
  surveyController,
} from './services';

// =====================
// STATE
// =====================

const page = document.body.dataset.page || '';
let selectedBranch: 'a' | 'b' | null = null;

// =====================
// HELPERS
// =====================

function setStoredCodeOnScreen(code: string): void {
  const codeEl = document.getElementById('code') as HTMLElement | null;
  const finalCodeEl = document.getElementById('finalCode') as HTMLElement | null;

  if (codeEl) codeEl.textContent = code.toUpperCase();

  if (finalCodeEl) finalCodeEl.textContent = code.toUpperCase();

}

function restoreFinalScreenIfCodeAlreadyExists(flow: FlowController): void {
  if (page !== 'final') return;

  const existingCode = localStorage.getItem('currentCode');

  if (!existingCode) return;

  setStoredCodeOnScreen(existingCode);
  flow.showStep(1);
}


function setupRatings() {
  const bars = document.querySelectorAll('.rating-bar') as NodeListOf<HTMLElement>;

  const thumbPositions: Record<string, string> = {
    '1': '10%',
    '2': '30%',
    '3': '50%',
    '4': '70%',
    '5': '90%',
  };

  const fillPositions: Record<string, string> = {
    '1': '10%',
    '2': '30%',
    '3': '50%',
    '4': '70%',
    '5': '100%',
  };

  bars.forEach((bar) => {
    const hiddenInput = bar.querySelector('input[type="hidden"]') as HTMLInputElement | null;
    const steps = bar.querySelectorAll('.rating-step') as NodeListOf<HTMLButtonElement>;
    const thumb = bar.querySelector('.rating-thumb') as HTMLElement | null;

    const setValue = (value: string) => {
      bar.dataset.rating = value;
      bar.style.setProperty('--thumb-left', thumbPositions[value]);
      bar.style.setProperty('--fill', fillPositions[value]);

      if (hiddenInput) {
        hiddenInput.value = value;
      }

      const current = Number(value);

      steps.forEach((step) => {
        const stepValue = Number(step.dataset.value);
        step.classList.remove('active', 'passed');

        if (stepValue === current) {
          step.classList.add('active');
        } else if (stepValue < current) {
          step.classList.add('passed');
        }
      });

      if (thumb) {
        thumb.style.left = thumbPositions[value];
      }
    };

    setValue(bar.dataset.rating || '1');

    steps.forEach((step) => {
      step.addEventListener('click', () => {
        setValue(step.dataset.value || '1');
      });
    });
  });
}
// =====================
// INIT
// =====================

document.addEventListener('DOMContentLoaded', () => {
  guardPageAccess(page);

  const versionEl = document.getElementById('version');

  if (versionEl) {
    versionEl.innerHTML = 'v1.5.03.1';
  }

  const config = getPageConfig(page);

  const flow = new FlowController({
    stepSelector: config.stepSelector,
    validateStep: (index) => {
      if (page === 'intro') return validateIntroForm(index);
      return validateZoneCode(index, page);
    },
    onFinish: () => {
      if (page === 'intro') {
        localStorage.setItem('intro-completed', 'true');
      }

      if (!isZoneCompleted(page) && page !== 'intro') {
        console.log('Você precisa concluir esta zona antes de avançar.');
        return;
      }

      if (config.nextUrl) {
        window.location.href = config.nextUrl;
      }
    },
  });

  flow.init();

  setupInputMasks();
  setupRatings();
  restoreFinalScreenIfCodeAlreadyExists(flow);

  // =====================
  // INTRO FLOW
  // =====================

  const introPage = document.getElementById('intro') as HTMLElement | null;
  const introContinueBtn = document.querySelector('#intro [data-action="next"]') as HTMLButtonElement | null;

  introContinueBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    introPage?.classList.add('hidden');
    introPage?.classList.remove('active');
    window.scrollTo(0, 0);
    flow.showStep(1);
  });

  // =====================
  // MODAL CODE FLOW
  // =====================

  const correctBtn = document.getElementById('correct');
  const incorrectBtn = document.getElementById('incorrect');
  const modalContainer = document.querySelector('.modal-container');
  const correctDiv = document.getElementById('correctCodeDiv');
  const incorrectDiv = document.getElementById('incorrectCodeDiv');

  if (page !== 'final') {
    correctBtn?.addEventListener('click', () => {
      modalContainer?.classList.add('hidden');
      correctDiv?.classList.add('hidden');

      markZoneAsCompleted(page);
      window.scrollTo(0, 0);

      if (typeof config.afterCodeStep === 'number') {
        flow.showStep(config.afterCodeStep);
      }
    });

    incorrectBtn?.addEventListener('click', () => {
      modalContainer?.classList.add('hidden');
      incorrectDiv?.classList.add('hidden');
    });
  }

  // =====================
  // FINAL FLOW
  // =====================

  if (page === 'final') {
    const finalContinueBtn = document.getElementById('finalContinue') as HTMLButtonElement | null;
    const finalModalContainer = document.querySelector('.modal-container.final') as HTMLElement | null;
    const finalCorrectBtn = document.getElementById('correct') as HTMLButtonElement | null;
    const retrySendButton = document.getElementById('retrySendButton') as HTMLButtonElement | null;

    surveyController.setupFinalSurveyCheckboxes();

    finalContinueBtn?.addEventListener('click', async (event) => {
      event.preventDefault();
      await surveyController.saveFinalSurvey();
      finalModalContainer?.classList.remove('hidden');
    });

    finalCorrectBtn?.addEventListener('click', async () => {
      await handleFinalCodeGeneration(flow);
    });

    retrySendButton?.addEventListener('click', async () => {
      await handleManualRetry(flow);
    });
  }

  // =====================
  // BRANCH FLOW (NEO / OPEN)
  // =====================

  if (config.hasBranchQuestion && typeof config.afterBranchStep === 'number') {
    setupBranchQuestion(flow, config.afterBranchStep, (branch) => {
      selectedBranch = branch;
    });
  }

  // =====================
  // SHORT FLOW (SHADOW / HERO)
  // =====================

  if (config.isShortFlow) {
    const buttons = document.querySelectorAll('#question [data-action="next"]') as NodeListOf<HTMLButtonElement>;

    buttons.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        window.scrollTo(0, 0);
        flow.showStep(2);
      });
    });
  }

  // =====================
  // SAVE SURVEY
  // ======================

  if (page === 'neozone') {
    const questionContinueBtn = document.getElementById('question-continue') as HTMLButtonElement | null;
    const confirmNeoZoneSurveyBtn = document.getElementById('confirmNeoZoneSurvey') as HTMLButtonElement | null;
    const skipNvidiaEvaluationBtn = document.getElementById('skipNvidiaEvaluation') as HTMLButtonElement | null;

    const optionAInput = document.getElementById('option-a') as HTMLInputElement | null;
    const optionBInput = document.getElementById('option-b') as HTMLInputElement | null;

    const optionACheckedDiv = document.querySelector('.option-a-checked') as HTMLElement | null;
    const optionBCheckedDiv = document.querySelector('.option-b-checked') as HTMLElement | null;

    const evaluateFromOptionBBtn = document.querySelector(
      '.option-b-checked .button.bigger.neozone'
    ) as HTMLButtonElement | null;

    function showOptionAContent() {
      optionACheckedDiv?.classList.remove('hidden');
      optionBCheckedDiv?.classList.add('hidden');
    }

    function showOptionBContent() {
      optionBCheckedDiv?.classList.remove('hidden');
      optionACheckedDiv?.classList.add('hidden');
    }

    questionContinueBtn?.addEventListener('click', () => {
      if (optionAInput?.checked) {
        selectedBranch = 'a';
        surveyController.saveNeoZoneBranch('a');
        showOptionAContent();
        return;
      }

      if (optionBInput?.checked) {
        selectedBranch = 'b';
        surveyController.saveNeoZoneBranch('b');
        showOptionBContent();
        return;
      }
    });

    skipNvidiaEvaluationBtn?.addEventListener('click', async (event) => {
      event.preventDefault();

      const textarea = document.getElementById('whyDitintEvaluate') as HTMLTextAreaElement | null;
      const errorSpan = document.querySelector('.option-b-checked .error-span') as HTMLElement | null;

      const value = textarea?.value.trim() || '';

      if (!value) {
        errorSpan?.classList.remove('hidden');
        errorSpan?.classList.add('show');
        return;
      }

      errorSpan?.classList.add('hidden');
      errorSpan?.classList.remove('show');

      surveyController.saveNeoZoneBranch('b');
      await surveyController.saveNeoZoneRatings();

      window.scrollTo(0, 0);
      flow.showStep(4); // vai direto para zoneIntro
    });

    evaluateFromOptionBBtn?.addEventListener('click', (event) => {
      event.preventDefault();

      showOptionAContent();

      flow.showStep(3); // vai para question-3
    });

    confirmNeoZoneSurveyBtn?.addEventListener('click', async () => {
      surveyController.saveNeoZoneBranch('a');
      await surveyController.saveNeoZoneRatings();
    });
  }

  if (page === 'openzone') {
    const confirmOpenZoneSurveyBtn = document.getElementById('confirmOpenZoneSurvey') as HTMLButtonElement | null;

    confirmOpenZoneSurveyBtn?.addEventListener('click', async () => {
      await surveyController.saveOpenZoneRatings();
    });
  }

  console.log('Página:', page, '| Próxima:', config.nextUrl);
});